import type { GetGameStateResponse, GameNotification } from '~/composables/useApi'
import type { SceneInfo } from '~/types/interfaces'

/**
 * scenes 未ロード時 / 未知 sceneId のフォールバック（羽根筆のゆりかご相当のダミー）。
 * getSceneInfo() が scene を引けなかったときに返す。
 */
const dummySceneInfo: SceneInfo = {
  sceneId: 'cmp_cradle',
  name: '羽根筆のゆりかご',
  description: '羽根筆のゆりかご',
  disabled: false,
  isVisible: false,
  isMarked: false,
  isUnlocked: false,
  isPurchased: false,
  isCleared: false,
  parentId: '',
  enableGoddessJudgement: false,
  coordX: 0,
  coordY: 0,
}

/**
 * ゲーム状態の単一ソース（#392 / R5 で composable `useGameState` を廃止し統合）。
 *
 * setup store にしているのは、状態 + getter に加えて **API を叩くアクション**
 * （reloadGameState / updateUserName）をここに集約するため。`useApi()` は
 * `useRuntimeConfig()` に依存し **setup コンテキストでの生成が必須**なので、
 * options store の action 内（イベントハンドラ由来 = コンテキスト外）で
 * `useApi()` を呼ぶと "[nuxt] instance unavailable" になる。setup store なら
 * ストア初期化時（＝最初の `useGameStateStore()`、常に setup コンテキスト）に
 * 1 度だけ `useApi()` を生成してクロージャで抱えられる。
 */
export const useGameStateStore = defineStore('gameState', () => {
  const api = useApi()

  // --- state ---
  const isLoaded = ref(false)
  const isDirty = ref(true)
  const state = ref<GetGameStateResponse | null>(null)
  const notificationQueue = ref<string[]>([])

  // --- getters ---
  const user = computed(() => state.value?.user)
  const scenes = computed(() => state.value?.scenes)
  const rules = computed(() => state.value?.rules)
  const notifications = computed(() => state.value?.notifications)
  const advisorCosts = computed(() => state.value?.advisorCosts ?? [])
  // 羽根筆が書いたときに取る MP（#938）
  const quillMpCost = computed(() => state.value?.quillMpCost ?? 0)
  // 冒険の手記（#900）。未ロード時は undefined（一覧は開かれたら reload する）。
  const memoirs = computed(() => state.value?.memoirs)

  // --- mutations ---
  function setState(data: GetGameStateResponse) {
    state.value = data
    isLoaded.value = true
    isDirty.value = false

    // SSR では setTimeout がクライアントに伝わらないため、
    // クライアントサイドでのみ通知をキューに投入する
    if (import.meta.client) {
      setTimeout(() => {
        pushNotifications(state.value?.notifications || [])
      }, 3000)
    }
  }

  /**
   * Reset the game state from the server.
   * Must transit to the initial scene after the reset.
   */
  function reset() {
    isLoaded.value = false
    isDirty.value = true
    state.value = null
    notificationQueue.value = []
  }

  function pushNotifications(notifs: GameNotification[]) {
    notificationQueue.value.push(...notifs.map(n => n.body))
  }

  // --- API-backed actions（旧 useGameState composable から統合。#392）---
  async function reloadGameState() {
    try {
      const resp = await api.getGameState()
      if (!resp) {
        throw new Error('Failed to load game state.')
      }
      setState(resp)
    }
    catch (e) {
      console.error('[gameState] reloadGameState error', e)
      throw e
    }
  }

  /** dirty のときだけサーバから読み直す（route middleware から使う）。 */
  async function confirm() {
    if (isDirty.value) {
      return reloadGameState()
    }
  }

  async function updateUserName(name: string) {
    try {
      await api.updateUserAttribute({ name })
      if (user.value) {
        user.value.name = name
      }
    }
    catch (e) {
      console.error('[gameState] updateUserName error', e)
      throw e
    }
  }

  /** sceneId から SceneInfo を引く。未ロード / 未知 ID は dummySceneInfo。 */
  function getSceneInfo(sceneId: string): SceneInfo {
    return scenes.value?.[sceneId] || dummySceneInfo
  }

  return {
    // state
    isLoaded,
    isDirty,
    state,
    notificationQueue,
    // getters
    user,
    scenes,
    rules,
    notifications,
    advisorCosts,
    quillMpCost,
    memoirs,
    // mutations
    setState,
    reset,
    pushNotifications,
    // API-backed actions
    reloadGameState,
    confirm,
    updateUserName,
    getSceneInfo,
  }
})
